import { basicAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const redHatSatellite = defineConnector({
  identifier: "redHatSatellite",
  name: "Red Hat Satellite",
  tagLine: "Infrastructure and software lifecycle management",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://satellite.example.com/api"),
  auth: basicAuth({
    username: { from: "config", field: "username" },
    password: { from: "secret", field: "personalAccessToken" },
  }),
  steps: [
    {
      title: "Set up Red Hat Satellite",
      items: [
        {
          kind: "markdown",
          content:
            "In Satellite, open **Administer → Users**, select a user with read permissions, and add a personal access token. Copy it when shown. Enter the server’s API URL, username, and token below. See [API authentication](https://docs.redhat.com/en/documentation/red_hat_satellite/6.18/html/using_the_satellite_rest_api/api-call-authentication).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "url",
          label: "API base URL",
          required: true,
          placeholder: "https://satellite.example.com/api",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Replace satellite.example.com with the fully qualified hostname of your Satellite Server. Red Hat documents the API route under /api and provides the built-in API reference at https://satellite.example.com/apidoc/v2.html. Satellite API version 2 is supported and is the default route version.",
        },
        {
          kind: "input",
          label: "Satellite username",
          required: true,
          target: {
            kind: "configuration",
            field: "username",
          },
          helperText:
            "The Red Hat Satellite username used for API access. Assign the user roles with read permissions for the resources that the connector will query.",
        },
        {
          kind: "input",
          label: "Personal access token",
          required: true,
          target: {
            kind: "secrets",
            field: "personalAccessToken",
          },
          helperText:
            "Create this token in Satellite at Administer → Users → select the user → Personal Access Tokens → Add Personal Access Token. Copy it when created because Satellite does not show it again after leaving the page.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://satellite.example.com/api")
          .trim()
          .replace(/\/+$/, "") + "/status",
      ...{ method: "GET" },
    }),
  },
});
